import React from 'react';

function SectionWrap({ title, mandatory, visible, onToggle, children, badge }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800">{title}</span>
          {badge && <span className="text-xs text-gray-400">{badge}</span>}
        </div>
        {mandatory ? (
          <span className="text-xs text-gray-400">Verplicht</span>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className={`text-xs px-2.5 py-0.5 rounded-full transition-colors ${
              visible
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
            }`}
          >
            {visible ? 'Zichtbaar' : 'Verborgen'}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

export default function NewsletterEditorV2({ newsletter_v2, onChange }) {
  if (!newsletter_v2) return null;

  const {
    header:   h  = {},
    intro:    it = {},
    sections       = [],
    deals          = [],
    cta            = {},
    signoff:  sg   = {},
  } = newsletter_v2;

  const set = (section, field, value) =>
    onChange({ ...newsletter_v2, [section]: { ...newsletter_v2[section], [field]: value } });

  // — Sections —
  const updateSection = (i, field, value) => {
    const arr = [...sections];
    arr[i] = { ...arr[i], [field]: value || null };
    onChange({ ...newsletter_v2, sections: arr });
  };
  const addSection = () => {
    if (sections.length >= 3) return;
    onChange({ ...newsletter_v2, sections: [...sections, { title: '', body: '', photo_url: null, highlight: null }] });
  };
  const removeSection = (i) =>
    onChange({ ...newsletter_v2, sections: sections.filter((_, idx) => idx !== i) });

  // — Deals —
  const updateDeal = (i, field, value) => {
    const arr = [...deals];
    arr[i] = { ...arr[i], [field]: value || null };
    onChange({ ...newsletter_v2, deals: arr });
  };
  const addDeal = () => {
    if (deals.length >= 2) return;
    onChange({ ...newsletter_v2, deals: [...deals, { destination: '', price_from: null, tagline: '' }] });
  };
  const removeDeal = (i) =>
    onChange({ ...newsletter_v2, deals: deals.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">

      {/* — E-mail header — */}
      <SectionWrap title="E-mail header" mandatory>
        <div className="p-4 space-y-3">
          <div>
            <label className="label text-xs">Onderwerpregel</label>
            <input className="input text-sm" value={h.subject || ''}
              onChange={e => set('header', 'subject', e.target.value)}
              placeholder="Bijv. Zomerse aanbiedingen — pak nu uw droomvakantie!" />
          </div>
          <div>
            <label className="label text-xs">Preheader (e-mailpreview)</label>
            <input className="input text-sm" value={h.preheader || ''}
              onChange={e => set('header', 'preheader', e.target.value)}
              placeholder="Korte preview-tekst, max 90 tekens" />
          </div>
          <div>
            <label className="label text-xs">Hoofdtitel</label>
            <input className="input text-sm" value={h.headline || ''}
              onChange={e => set('header', 'headline', e.target.value)}
              placeholder="Bijv. Uw zomervakantie begint hier" />
          </div>
        </div>
      </SectionWrap>

      {/* — Intro — */}
      <SectionWrap title="Intro" mandatory>
        <div className="p-4">
          <textarea className="input text-sm min-h-20 resize-none" value={it.body || ''}
            onChange={e => set('intro', 'body', e.target.value)}
            placeholder="Warme openingsparagraaf..." />
        </div>
      </SectionWrap>

      {/* — Content sections — */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <span className="text-sm font-medium text-gray-800">
            Secties <span className="text-gray-400 font-normal">({sections.length}/3)</span>
          </span>
          {sections.length < 3 && (
            <button type="button" onClick={addSection}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors">
              + Sectie toevoegen
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-100">
          {sections.map((s, i) => (
            <div key={i} className="p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Sectie {i + 1}</span>
                <button type="button" onClick={() => removeSection(i)}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                  Verwijderen
                </button>
              </div>
              <div>
                <label className="label text-xs">Titel</label>
                <input className="input text-sm" value={s.title || ''}
                  onChange={e => updateSection(i, 'title', e.target.value)}
                  placeholder="Sectietitel..." />
              </div>
              <div>
                <label className="label text-xs">Tekst</label>
                <textarea className="input text-sm min-h-16 resize-none" value={s.body || ''}
                  onChange={e => updateSection(i, 'body', e.target.value)}
                  placeholder="Sectietekst..." />
              </div>
              <div>
                <label className="label text-xs">Highlight / citaat (optioneel)</label>
                <input className="input text-sm" value={s.highlight || ''}
                  onChange={e => updateSection(i, 'highlight', e.target.value)}
                  placeholder="Korte pull-quote of sleutelzin..." />
              </div>
              <div>
                <label className="label text-xs">Foto URL (optioneel)</label>
                <input className="input text-sm" value={s.photo_url || ''}
                  onChange={e => updateSection(i, 'photo_url', e.target.value)}
                  placeholder="https://..." />
                {s.photo_url && (
                  <img src={s.photo_url} alt=""
                    className="w-full h-24 object-cover rounded-lg mt-2"
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
              </div>
            </div>
          ))}
          {sections.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-400">
              Geen secties — klik "+ Sectie toevoegen"
            </div>
          )}
        </div>
      </div>

      {/* — Deals — */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
          <span className="text-sm font-medium text-gray-800">
            Aanbiedingen <span className="text-gray-400 font-normal">({deals.length}/2)</span>
          </span>
          {deals.length < 2 && (
            <button type="button" onClick={addDeal}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors">
              + Aanbieding toevoegen
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-100">
          {deals.map((d, i) => (
            <div key={i} className="p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Aanbieding {i + 1}</span>
                <button type="button" onClick={() => removeDeal(i)}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                  Verwijderen
                </button>
              </div>
              <div>
                <label className="label text-xs">Bestemming</label>
                <input className="input text-sm" value={d.destination || ''}
                  onChange={e => updateDeal(i, 'destination', e.target.value)}
                  placeholder="Bijv. Mallorca" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label text-xs">Prijs vanaf (€)</label>
                  <input className="input text-sm" value={d.price_from || ''}
                    onChange={e => updateDeal(i, 'price_from', e.target.value)}
                    placeholder="499" />
                </div>
                <div>
                  <label className="label text-xs">Tagline</label>
                  <input className="input text-sm" value={d.tagline || ''}
                    onChange={e => updateDeal(i, 'tagline', e.target.value)}
                    placeholder="Alles inbegrepen" />
                </div>
              </div>
            </div>
          ))}
          {deals.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-400">
              Geen aanbiedingen — optioneel
            </div>
          )}
        </div>
      </div>

      {/* — CTA — */}
      <SectionWrap title="Call-to-action"
        visible={!!cta.visible}
        onToggle={() => set('cta', 'visible', !cta.visible)}>
        {cta.visible && (
          <div className="p-4 space-y-2.5">
            <div>
              <label className="label text-xs">Tekst</label>
              <input className="input text-sm" value={cta.text || ''}
                onChange={e => set('cta', 'text', e.target.value)}
                placeholder="Plan een afspraak" />
            </div>
            <div>
              <label className="label text-xs">Opmerking (optioneel)</label>
              <input className="input text-sm" value={cta.note || ''}
                onChange={e => set('cta', 'note', e.target.value || null)}
                placeholder="Vrijblijvend adviesgesprek" />
            </div>
            <div>
              <label className="label text-xs">URL (optioneel)</label>
              <input className="input text-sm" value={cta.url || ''}
                onChange={e => set('cta', 'url', e.target.value || null)}
                placeholder="https://..." />
            </div>
          </div>
        )}
      </SectionWrap>

      {/* — Signoff — */}
      <SectionWrap title="Afsluiting" mandatory>
        <div className="p-4">
          <textarea className="input text-sm min-h-16 resize-none" value={sg.body || ''}
            onChange={e => set('signoff', 'body', e.target.value)}
            placeholder="Warme afsluiting..." />
        </div>
      </SectionWrap>

    </div>
  );
}
